import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Menu, Search, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Wordmark } from '@/components/generals/brand'

import { HUBS } from '@/constants/hubs'

const NAV_LINKS = [
    { href: '/guides', label: 'Guides' },
    ...HUBS.map(hub => ({ href: `/${hub.slug}`, label: hub.name })),
    { href: '/about', label: 'About' }
]

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    const path = router.asPath.split('?')[0].split('#')[0]
    const isActive = (href: string) => path === href || path.startsWith(`${href}/`)

    return (
        <header className='sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur'>
            <Container className='flex h-16 items-center justify-between gap-4'>
                <Wordmark />

                <nav className='hidden items-center gap-1 md:flex' aria-label='Primary'>
                    {NAV_LINKS.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            className={cn(
                                'rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
                                isActive(link.href) && 'text-foreground'
                            )}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <form action='/search' method='get' role='search' className='hidden lg:block'>
                        <div className='relative'>
                            <Search className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                            <input
                                type='search'
                                name='q'
                                placeholder='Search articles'
                                aria-label='Search articles'
                                className='h-9 w-48 rounded-full border border-border bg-card pl-9 pr-3 text-sm outline-none transition-[width] placeholder:text-muted-foreground focus:w-60 focus:ring-2 focus:ring-ring/30'
                            />
                        </div>
                    </form>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        aria-expanded={open}
                        className='inline-flex size-9 items-center justify-center rounded-md text-foreground hover:bg-secondary md:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </Container>

            {open && (
                <div className='border-t border-border/70 bg-background md:hidden'>
                    <Container className='flex flex-col gap-1 py-4'>
                        <form action='/search' method='get' role='search' className='mb-2'>
                            <div className='relative'>
                                <Search className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                                <input
                                    type='search'
                                    name='q'
                                    placeholder='Search articles'
                                    aria-label='Search articles'
                                    className='h-10 w-full rounded-full border border-border bg-card pl-9 pr-3 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring/30'
                                />
                            </div>
                        </form>

                        {NAV_LINKS.map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                onClick={() => setOpen(false)}
                                className={cn(
                                    'rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground',
                                    isActive(link.href) && 'bg-secondary text-foreground'
                                )}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </Container>
                </div>
            )}
        </header>
    )
}
